import type { BaseInputTemplateProps, WidgetProps } from '@rjsf/utils';
import { MenuItem, TextField } from '@mui/material';
import { generateTemplates } from '@rjsf/mui';
import { ariaDescribedByIds, enumOptionsIndexForValue, enumOptionsValueForIndex, labelValue } from '@rjsf/utils';

// 커스텀 BaseInputTemplate: shrink 강제, title(tooltip) 제거
const defaultTemplates = generateTemplates();
const DefaultBaseInput = defaultTemplates.BaseInputTemplate!;
const CustomBaseInputTemplate = ({ InputLabelProps, ...rest }: BaseInputTemplateProps) => (
  <DefaultBaseInput {...rest} InputLabelProps={{ ...InputLabelProps, shrink: true }} inputProps={{ title: '' }} />
);

export const formTemplates = { ...defaultTemplates, BaseInputTemplate: CustomBaseInputTemplate };

// 커스텀 SelectWidget: placeholder를 옵션 목록이 아닌 renderValue로 표시
const CustomSelectWidget = ({
  id,
  options,
  label,
  hideLabel,
  required,
  disabled,
  readonly,
  value,
  multiple: multipleProp,
  autofocus,
  onChange,
  onBlur,
  onFocus,
  rawErrors = [],
  InputLabelProps,
  SelectProps,
  autocomplete,
}: WidgetProps) => {
  const { enumOptions, enumDisabled, emptyValue: optEmptyVal } = options;
  const placeholderText = (options.placeholderText ?? '') as string;
  const multiple = typeof multipleProp === 'undefined' ? false : !!multipleProp;
  const emptyValue = multiple ? [] : '';
  const isEmpty = typeof value === 'undefined' || (!multiple && value === emptyValue);
  const selectedIndexes = enumOptionsIndexForValue(value, enumOptions, multiple);
  const hasError = rawErrors.length > 0;

  return (
    <TextField
      id={id}
      name={id}
      label={labelValue(label || undefined, hideLabel, undefined)}
      value={!isEmpty && typeof selectedIndexes !== 'undefined' ? selectedIndexes : emptyValue}
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      autoComplete={autocomplete}
      error={hasError}
      onChange={({ target: { value: val } }) => onChange(enumOptionsValueForIndex(val, enumOptions, optEmptyVal))}
      onBlur={({ target }) => onBlur(id, enumOptionsValueForIndex(target?.value, enumOptions, optEmptyVal))}
      onFocus={({ target }) => onFocus(id, enumOptionsValueForIndex(target?.value, enumOptions, optEmptyVal))}
      select
      InputLabelProps={{ ...InputLabelProps, shrink: true }}
      SelectProps={{
        ...SelectProps,
        multiple,
        displayEmpty: true,
        renderValue: selected => {
          const isEmptySelection = selected === '' || selected === undefined;
          if (isEmptySelection) {
            return <span style={hasError ? { color: '#d32f2f' } : { color: 'rgba(0, 0, 0, 0.42)' }}>{placeholderText}</span>;
          }
          return enumOptions?.[Number(selected)]?.label ?? '';
        },
      }}
      aria-describedby={ariaDescribedByIds(id)}
    >
      {Array.isArray(enumOptions) &&
        enumOptions.map(({ value: optValue, label: optLabel }, i) => {
          const isOptDisabled = Array.isArray(enumDisabled) && enumDisabled.indexOf(optValue) !== -1;
          return (
            <MenuItem key={String(optValue)} value={String(i)} disabled={isOptDisabled}>
              {optLabel}
            </MenuItem>
          );
        })}
    </TextField>
  );
};

export const formWidgets = { SelectWidget: CustomSelectWidget };
